// biome-ignore-all lint/suspicious/useIterableCallbackReturn: TODO - address later
/**
 * Utilities for managing saved view state
 *
 * These functions work with organization-scoped browser storage keys
 * to match the pattern used by useBrowserStorage hooks.
 */

// Baseline comparison approach - no longer need loading state management

interface BrowserState {
  localStorage: Record<string, unknown>;
  sessionStorage: Record<string, unknown>;
  url: string;
  page: string;
}

/**
 * Build an organization-scoped storage key
 */
function buildOrgKey(organizationId: string, key: string): string {
  return `${organizationId}::${key}`;
}

/**
 * Capture current browser state for saving
 *
 * This function:
 * 1. Captures ONLY keys matching the page scope (e.g., "campaigns::list::")
 * 2. Stores keys WITHOUT the org prefix (for portability)
 * 3. Excludes saved-views metadata keys (they're not part of the view config)
 */
export function captureBrowserState(
  pageType: string,
  organizationId: string,
): Record<string, unknown> {
  const state: BrowserState = {
    localStorage: {},
    sessionStorage: {},
    url: window.location.pathname + window.location.search,
    page: window.location.pathname, // Store the actual path for navigation
  };

  if (!organizationId || !pageType)
    return state as unknown as Record<string, unknown>;

  const orgPrefix = `${organizationId}::`;

  /**
   * Checks if a storage key is a "shared" key at ANY ancestor scope level.
   *
   * A shared key's prefix must be a STRICT prefix of the pageType,
   * meaning the key lives "above" the current page in the hierarchy.
   *
   * For pageType "reports::analytics::summary":
   *   "reports::shared-key"              ✓ grandparent level
   *   "reports::analytics::shared-key"   ✓ parent level
   *   "reports::analytics::summary::key" ✗ direct page key (separate check)
   *   "reports::other::shared-key"       ✗ cousin branch (excluded)
   */
  const isAncestorSharedKey = (keyWithoutOrg: string): boolean => {
    const keySegments = keyWithoutOrg.split("::");
    const pageSegments = pageType.split("::");

    // Key prefix is all segments except the last (the actual key name)
    const keyPrefixLength = keySegments.length - 1;

    // Must have at least one prefix segment
    if (keyPrefixLength < 1) return false;
    // Must be strictly shorter than pageType (ancestor, not same level or deeper)
    if (keyPrefixLength >= pageSegments.length) return false;

    // Check that key prefix matches the corresponding pageType segments
    for (let i = 0; i < keyPrefixLength; i++) {
      if (keySegments[i] !== pageSegments[i]) return false;
    }

    return true;
  };

  // Helper to check if a key matches the page scope
  const matchesPageScope = (key: string): boolean => {
    // Remove org prefix to check the actual key
    const keyWithoutOrg = key.substring(orgPrefix.length);

    // Do not capture user-specific recent lists
    if (keyWithoutOrg.includes("::recent")) return false;

    // Check if it starts with the page type (e.g., "campaigns::list::")
    if (keyWithoutOrg.startsWith(`${pageType}::`)) return true;

    // Check if it's a shared key at an ancestor level
    return isAncestorSharedKey(keyWithoutOrg);
  };

  // Capture localStorage (only org-scoped keys for this page)
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(orgPrefix) && matchesPageScope(key)) {
      const value = localStorage.getItem(key);
      if (value) {
        try {
          // Store without the org prefix (like ShareButton does)
          const keyWithoutOrg = key.substring(orgPrefix.length);
          state.localStorage[keyWithoutOrg] = JSON.parse(value);
        } catch {
          // If not valid JSON, store as string
          const keyWithoutOrg = key.substring(orgPrefix.length);
          state.localStorage[keyWithoutOrg] = value;
        }
      }
    }
  }

  // Capture sessionStorage (only org-scoped keys for this page)
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key?.startsWith(orgPrefix) && matchesPageScope(key)) {
      const value = sessionStorage.getItem(key);
      if (value) {
        try {
          // Store without the org prefix (like ShareButton does)
          const keyWithoutOrg = key.substring(orgPrefix.length);
          state.sessionStorage[keyWithoutOrg] = JSON.parse(value);
        } catch {
          // If not valid JSON, store as string
          const keyWithoutOrg = key.substring(orgPrefix.length);
          state.sessionStorage[keyWithoutOrg] = value;
        }
      }
    }
  }

  return state as unknown as Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Org-scoped storage helpers (exported)
// ---------------------------------------------------------------------------

/** Build an organization-scoped storage key */
export function buildOrgScopedKey(organizationId: string, key: string): string {
  return key.startsWith(`${organizationId}::`)
    ? key
    : `${organizationId}::${key}`;
}

/**
 * Set an org-scoped storage item (JSON-serialized) and dispatch change event
 */
export function setOrgScopedItem(
  organizationId: string,
  storageType: "localStorage" | "sessionStorage",
  key: string,
  value: unknown,
): void {
  if (typeof window === "undefined") return;
  const fullKey = buildOrgScopedKey(organizationId, key);
  const storage =
    storageType === "localStorage" ? localStorage : sessionStorage;
  const oldValue = storage.getItem(fullKey);
  const newValue = JSON.stringify(value);
  storage.setItem(fullKey, newValue);
  dispatchStorageChangeEvent(fullKey, storageType, newValue, oldValue);
}

/**
 * Merge shared value with existing storage value and dispatch change event
 * - Arrays: merge by id (replace existing items with same id)
 * - Objects: shallow merge (shared values take precedence)
 * - Other types: replace
 */
export function mergeOrgScopedItem(
  organizationId: string,
  storageType: "localStorage" | "sessionStorage",
  key: string,
  sharedValue: unknown,
): void {
  if (typeof window === "undefined") return;
  const fullKey = buildOrgScopedKey(organizationId, key);
  const storage =
    storageType === "localStorage" ? localStorage : sessionStorage;
  const existing = storage.getItem(fullKey);

  if (!existing) {
    setOrgScopedItem(organizationId, storageType, key, sharedValue);
    return;
  }

  try {
    const existingValue = JSON.parse(existing);
    let mergedValue: unknown;

    if (Array.isArray(sharedValue) && Array.isArray(existingValue)) {
      const sharedIds = new Set(
        (sharedValue as Array<{ id?: string }>)
          .map((item) => item?.id)
          .filter(Boolean),
      );
      const existingItemsNotInShared = (
        existingValue as Array<{ id?: string }>
      ).filter((item) => !sharedIds.has(item?.id));
      mergedValue = [...existingItemsNotInShared, ...sharedValue];
    } else if (
      typeof sharedValue === "object" &&
      typeof existingValue === "object" &&
      sharedValue !== null &&
      existingValue !== null
    ) {
      mergedValue = {
        ...(existingValue as Record<string, unknown>),
        ...(sharedValue as Record<string, unknown>),
      };
    } else {
      mergedValue = sharedValue;
    }

    const oldValue = existing;
    const newValue = JSON.stringify(mergedValue);
    storage.setItem(fullKey, newValue);
    dispatchStorageChangeEvent(fullKey, storageType, newValue, oldValue);
  } catch {
    setOrgScopedItem(organizationId, storageType, key, sharedValue);
  }
}

/**
 * Apply saved state to browser storage
 *
 * This function:
 * 1. Clears page-scoped sessionStorage keys (temporary session data)
 * 2. Preserves all localStorage keys (persistent data across sessions)
 * 3. Applies the new state from the saved view
 * 4. Preserves saved-views metadata (loaded-id, has-changes, etc.)
 */
export function applyBrowserState(
  state: Record<string, unknown>,
  organizationId: string,
  pageType: string,
): void {
  if (!organizationId) return;

  const orgPrefix = `${organizationId}::`;

  // pageType is required (e.g., "accounts::list")
  if (!pageType) return;

  try {
    /**
     * Checks if a storage key is a "shared" key at ANY ancestor scope level.
     * See isAncestorSharedKey in captureBrowserState for detailed documentation.
     */
    const isAncestorSharedKey = (keyWithoutOrg: string): boolean => {
      const keySegments = keyWithoutOrg.split("::");
      const pageSegments = pageType.split("::");

      const keyPrefixLength = keySegments.length - 1;

      if (keyPrefixLength < 1) return false;
      if (keyPrefixLength >= pageSegments.length) return false;

      for (let i = 0; i < keyPrefixLength; i++) {
        if (keySegments[i] !== pageSegments[i]) return false;
      }

      return true;
    };

    // Helper to check if a key belongs to this page scope and should be cleared
    const isPageScopedKey = (key: string): boolean => {
      if (!key.startsWith(orgPrefix)) return false;
      const keyWithoutOrg = key.substring(orgPrefix.length);

      // Never clear saved-views metadata
      if (keyWithoutOrg.startsWith("saved-views::")) return false;

      // Check if it's for this page scope (e.g., "campaigns::list::")
      if (keyWithoutOrg.startsWith(`${pageType}::`)) return true;

      // Check if it's a shared key at an ancestor level
      return isAncestorSharedKey(keyWithoutOrg);
    };

    // Only clear sessionStorage keys (temporary data)
    // Leave localStorage keys alone (persistent data that should survive across sessions)
    const keysToRemove: string[] = [];

    // Only find sessionStorage keys to remove
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && isPageScopedKey(key)) {
        keysToRemove.push(key);
      }
    }

    // Collect all storage operations to batch them
    const storageEvents: Array<() => void> = [];

    // Remove the sessionStorage keys (without dispatching events yet)
    keysToRemove.forEach((key) => {
      const oldSessionValue = sessionStorage.getItem(key);

      if (oldSessionValue !== null) {
        sessionStorage.removeItem(key);
        storageEvents.push(() =>
          dispatchStorageChangeEvent(
            key,
            "sessionStorage",
            null,
            oldSessionValue,
          ),
        );
      }
    });

    // Apply localStorage values (without dispatching events yet)
    if (state.localStorage) {
      Object.entries(state.localStorage).forEach(([key, value]) => {
        try {
          // Add org prefix if not already present (state keys might not have it)
          const fullKey = key.startsWith(orgPrefix)
            ? key
            : `${orgPrefix}${key}`;
          // Skip recent lists
          if (fullKey.includes("::recent")) return;
          const oldValue = localStorage.getItem(fullKey);
          // Always JSON stringify to ensure proper encoding
          const newValue = JSON.stringify(value);
          localStorage.setItem(fullKey, newValue);
          storageEvents.push(() =>
            dispatchStorageChangeEvent(
              fullKey,
              "localStorage",
              newValue,
              oldValue,
            ),
          );
        } catch {
          // Silently ignore localStorage errors
        }
      });
    }

    // Apply sessionStorage values (without dispatching events yet)
    if (state.sessionStorage) {
      Object.entries(state.sessionStorage).forEach(([key, value]) => {
        try {
          // Add org prefix if not already present (state keys might not have it)
          const fullKey = key.startsWith(orgPrefix)
            ? key
            : `${orgPrefix}${key}`;
          // Skip recent lists (defensive; recents typically in localStorage)
          if (fullKey.includes("::recent")) return;
          const oldValue = sessionStorage.getItem(fullKey);
          // Always JSON stringify to ensure proper encoding
          const newValue = JSON.stringify(value);
          sessionStorage.setItem(fullKey, newValue);
          storageEvents.push(() =>
            dispatchStorageChangeEvent(
              fullKey,
              "sessionStorage",
              newValue,
              oldValue,
            ),
          );
        } catch {
          // Silently ignore sessionStorage errors
        }
      });
    }

    // Dispatch all storage events
    storageEvents.forEach((fn) => fn());
  } finally {
    // Always signal completion to avoid leaving UI in loading state
    try {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.dispatchEvent(new CustomEvent("browser-state-applied"));
        });
      });
    } catch {
      // no-op
    }
  }
}

/**
 * Dispatch storage change event to notify components
 */
export function dispatchStorageChangeEvent(
  key: string,
  storageType: "localStorage" | "sessionStorage",
  newValue: string | null,
  oldValue: string | null,
): void {
  if (typeof window === "undefined") return;

  const event = new CustomEvent("browser-storage-change", {
    detail: {
      key,
      storageType,
      newValue,
      oldValue,
    },
  });

  // Dispatch synchronously so callers can precisely control completion ordering
  window.dispatchEvent(event);
}

/** Read an org-scoped item (JSON parsed) */
export function getOrgScopedItem(
  organizationId: string,
  storageType: "localStorage" | "sessionStorage",
  key: string,
): unknown | null {
  if (typeof window === "undefined") return null;
  const fullKey = buildOrgScopedKey(organizationId, key);
  const storage =
    storageType === "localStorage" ? localStorage : sessionStorage;
  const value = storage.getItem(fullKey);
  if (value === null) return null;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

/** Remove an org-scoped item and dispatch change event */
export function removeOrgScopedItem(
  organizationId: string,
  storageType: "localStorage" | "sessionStorage",
  key: string,
): void {
  if (typeof window === "undefined") return;
  const fullKey = buildOrgScopedKey(organizationId, key);
  const storage =
    storageType === "localStorage" ? localStorage : sessionStorage;
  const oldValue = storage.getItem(fullKey);
  storage.removeItem(fullKey);
  dispatchStorageChangeEvent(fullKey, storageType, null, oldValue);
}

/**
 * Reset org-scoped keys that match any of the provided patterns to values from nextState.
 * - Patterns match exact key or prefixes with ':' or '-'
 * - Keys in storage but not present in nextState for the given patterns are removed
 * - Dispatches browser-storage-change events for each change
 */
export function resetOrgScopedKeysByPatterns(
  organizationId: string,
  storageType: "localStorage" | "sessionStorage",
  keyPatterns: string[],
  nextState: Record<string, unknown> | undefined,
): void {
  try {
    if (!organizationId) return;
    const orgPrefix = `${organizationId}::`;
    const storage =
      storageType === "localStorage" ? localStorage : sessionStorage;
    const stateMap = (nextState || {}) as Record<string, unknown>;

    const matchesPattern = (key: string, pattern: string): boolean => {
      return (
        (key === pattern ||
          key.startsWith(`${pattern}:`) ||
          key.startsWith(`${pattern}-`)) &&
        // Never match recent lists; these are user-specific and should be preserved
        !key.includes("::recent")
      );
    };

    for (const pattern of keyPatterns) {
      // First, set keys that exist in nextState for this pattern
      for (const [key, value] of Object.entries(stateMap)) {
        if (matchesPattern(key, pattern)) {
          const fullKey = key.startsWith(orgPrefix)
            ? key
            : `${orgPrefix}${key}`;
          const oldValue = storage.getItem(fullKey);
          if (value !== undefined && value !== null) {
            const newValueStr = JSON.stringify(value);
            storage.setItem(fullKey, newValueStr);
            dispatchStorageChangeEvent(
              fullKey,
              storageType,
              newValueStr,
              oldValue,
            );
          } else {
            storage.removeItem(fullKey);
            dispatchStorageChangeEvent(fullKey, storageType, null, oldValue);
          }
        }
      }

      // Then remove keys that exist in current storage but not in nextState for this pattern
      const keysToRemove: string[] = [];
      for (let i = 0; i < storage.length; i++) {
        const fullKey = storage.key(i);
        if (fullKey?.startsWith(orgPrefix)) {
          const keyWithoutOrg = fullKey.substring(orgPrefix.length);
          if (
            matchesPattern(keyWithoutOrg, pattern) &&
            !(keyWithoutOrg in stateMap)
          ) {
            keysToRemove.push(fullKey);
          }
        }
      }
      for (const fullKey of keysToRemove) {
        const oldValue = storage.getItem(fullKey);
        storage.removeItem(fullKey);
        dispatchStorageChangeEvent(fullKey, storageType, null, oldValue);
      }
    }
  } finally {
    // Always signal completion to avoid leaving UI in loading state
    try {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.dispatchEvent(new CustomEvent("browser-state-applied"));
        });
      });
    } catch {
      // no-op
    }
  }
}

/**
 * Clear all page-scoped browser state (for system default)
 */
export function clearPageScopedState(
  pageType: string,
  organizationId: string,
): void {
  if (!organizationId || !pageType) {
    return;
  }

  const prefix = `${organizationId}::${pageType}::`;
  const keysToRemove: string[] = [];

  // Find all localStorage keys for this page scope
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (
      key?.startsWith(prefix) &&
      !key.includes("saved-views::") &&
      !key.includes("::recent")
    ) {
      keysToRemove.push(key);
    }
  }

  // Find all sessionStorage keys for this page scope
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (
      key?.startsWith(prefix) &&
      !key.includes("saved-views::") &&
      !key.includes("::recent")
    ) {
      keysToRemove.push(key);
    }
  }

  // Remove all the keys and dispatch events
  keysToRemove.forEach((key) => {
    const oldLocalValue = localStorage.getItem(key);
    const oldSessionValue = sessionStorage.getItem(key);

    if (oldLocalValue !== null) {
      localStorage.removeItem(key);
      dispatchStorageChangeEvent(key, "localStorage", null, oldLocalValue);
    }

    if (oldSessionValue !== null) {
      sessionStorage.removeItem(key);
      dispatchStorageChangeEvent(key, "sessionStorage", null, oldSessionValue);
    }
  });
}

/**
 * Get personal default view for a page (org-scoped)
 */
export function getPersonalDefaultViewId(
  pageType: string,
  organizationId: string,
): string | null {
  if (!organizationId || !pageType) return null;

  const key = buildOrgKey(organizationId, `saved-views::${pageType}::default`);
  const value = localStorage.getItem(key);

  if (!value) return null;

  try {
    // Try to parse as JSON (new format)
    return JSON.parse(value);
  } catch {
    // Fall back to raw string (backwards compatibility)
    return value;
  }
}

/**
 * Set personal default view for a page (org-scoped)
 */
export function setPersonalDefaultViewId(
  pageType: string,
  viewId: string | null,
  organizationId: string,
): void {
  if (!organizationId || !pageType) return;

  const key = buildOrgKey(organizationId, `saved-views::${pageType}::default`);
  const oldValue = localStorage.getItem(key);

  if (viewId) {
    const newValue = JSON.stringify(viewId);
    localStorage.setItem(key, newValue);
    dispatchStorageChangeEvent(key, "localStorage", newValue, oldValue);
  } else {
    localStorage.removeItem(key);
    dispatchStorageChangeEvent(key, "localStorage", null, oldValue);
  }
}
